import { FieldProps } from '@rjsf/utils';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import LookupApi from '../../api/LookupApi';
import { useSideChannelSubscription } from '../../util/useSideChannel';
import useResourceLoader from '../../util/useResourceLoader';
import { ORG_UNIT_TYPE_COLORS, getOrgUnitTypeColor } from '../../util/orgUnitTypeColors';
import { AccordionItem } from '../ui/Accordion';
import CustomBadge from '../CustomBadge';
import Loader from '../Loader';
import { Period, showsYearType } from './OcifRequiredSchedules/scheduleRowRules';
import { dueOnLabel, periodLabel, yearTypeLabel } from './reportingCadence';

type ParentSchedule = {
  schedule: string;
  period?: string;
  yearType?: string;
  dueOn?: string;
};

type ParentRequirement = {
  orgUnit: string;
  schedules?: ParentSchedule[];
};

const TYPE_SEPARATOR = ' - ';
const OCIF_ORG_UNIT_NAME = 'OCIF';

/**
 * The lookup sends the level as a single "Name - Type" string. Known type names are matched as a
 * suffix first, since a unit's name may itself contain the separator.
 */
function splitOrgUnitLabel(label: string): { name: string; typeName: string } {
  const knownType = Object.keys(ORG_UNIT_TYPE_COLORS).find(typeName => label.endsWith(`${TYPE_SEPARATOR}${typeName}`));
  if (knownType) return { name: label.slice(0, -(TYPE_SEPARATOR.length + knownType.length)), typeName: knownType };

  const separatorAt = label.lastIndexOf(TYPE_SEPARATOR);
  if (separatorAt < 0) return { name: label, typeName: '' };
  return { name: label.slice(0, separatorAt), typeName: label.slice(separatorAt + TYPE_SEPARATOR.length) };
}

// OCIF sits at the top of every hierarchy; when it has no schedules of its own it is just noise.
function isEmptyOcifLevel(level: ParentRequirement) {
  return !level.schedules?.length && splitOrgUnitLabel(level.orgUnit ?? '').name === OCIF_ORG_UNIT_NAME;
}

function ScheduleList({ schedules }: { schedules: ParentSchedule[] }) {
  if (!schedules.length) {
    return (
      <p className="text-muted small mb-0 px-3 py-2">
        <Trans>This level has no schedules.</Trans>
      </p>
    );
  }

  return (
    <ul className="list-none m-0 p-0 divide-y divide-gray-200 dark:divide-gray-600">
      {schedules.map((item, index) => (
        <li key={`${item.schedule}-${index}`} className="px-3 py-2">
          <div className="font-semibold">{item.schedule}</div>
          <div className="text-muted small">
            {[periodLabel(item.period), showsYearType(item.period as Period) ? yearTypeLabel(item.yearType) : '', dueOnLabel(item.dueOn)]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </li>
      ))}
    </ul>
  );
}

function OcifParentRequirementsField({ registry, schema }: FieldProps) {
  useLingui(); // re-render on locale change; labels come from the global t
  const rootFormData = useSideChannelSubscription(registry.formContext.sideChannel, 0) as { field?: { orgId?: number } } | undefined;
  const orgUnitId = rootFormData?.field?.orgId;

  const [allLevels, loading, error] = useResourceLoader<ParentRequirement[]>(
    async () => (orgUnitId ? (await LookupApi.getParentReportRequirements({ orgUnitId })) ?? [] : []),
    [orgUnitId],
    []
  );

  const levels = allLevels.filter(level => !isEmptyOcifLevel(level));

  const renderContent = () => {
    if (loading) return <Loader spinner size={35} />;

    if (error) {
      return (
        <p className="text-danger small mb-0">
          <Trans>Could not load the schedules inherited from higher levels.</Trans>
        </p>
      );
    }

    if (!levels.length) {
      return (
        <p className="text-muted small mb-0">
          <Trans>No schedule was found in this hierarchy.</Trans>
        </p>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        {levels.map((level, index) => {
          const { name, typeName } = splitOrgUnitLabel(level.orgUnit ?? '');
          const typeColor = getOrgUnitTypeColor(typeName);
          const schedules = level.schedules ?? [];

          return (
            <AccordionItem
              key={`${level.orgUnit}-${index}`}
              icon="chevron"
              leftAccentColor={typeColor || undefined}
              title={
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{name}</span>
                  {typeName && (
                    <CustomBadge color={typeColor || 'secondary'} pill>
                      {typeName}
                    </CustomBadge>
                  )}
                  <span className="text-muted small ml-auto">{schedules.length === 1 ? t`1 schedule` : t`${schedules.length} schedules`}</span>
                </span>
              }
            >
              <ScheduleList schedules={schedules} />
            </AccordionItem>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      {schema.title && <div className="form-subtitle mb-2">{schema.title}</div>}
      {renderContent()}
    </div>
  );
}

export default OcifParentRequirementsField;
